import modelNames from '@/ts/index';

/**
 * Columns whose record can be one of several models.
 *
 * The backend declares such a column in models.json as a union of model names. A station's
 * source is the example: `'ContactLoginCustomPlaylist | LocationZone | LocationZoneCustomPlaylist'`.
 * The id alone does not say which table it points into. Station 40 with source 812 plays zone 812
 * when its source is a zone, and custom playlist 812 when its source is a custom playlist. So each
 * indexed reference carries a `type` naming its model, and links must follow that, never a guess.
 */

/** The models a union column can hold, or null when the column is not a union of known models. */
export function polymorphicModelTypes(dataType: string | undefined | null): string[] | null {
    if (!dataType || !dataType.includes('|')) {
        return null;
    }
    const memberTypes = dataType.split('|').map((memberType) => memberType.trim());
    return memberTypes.every((memberType) => memberType in modelNames) ? memberTypes : null;
}

/**
 * The model a reference names through its `type`, when the union column allows it.
 * Null when the reference has no type, or names a model outside the union. A caller then
 * shows the value without a link, because a link by id alone opens an unrelated record.
 */
export function resolvePolymorphicModelType(dataType: string | undefined | null, reference: unknown): string | null {
    const memberTypes = polymorphicModelTypes(dataType);
    if (!memberTypes) {
        return null;
    }
    const referenceType =
        reference !== null && typeof reference === 'object' && !Array.isArray(reference)
            ? (reference as { type?: unknown }).type
            : undefined;
    return typeof referenceType === 'string' && memberTypes.includes(referenceType) ? referenceType : null;
}
